import type { FC, ReactNode } from 'react'
import type { CmsPage } from '@/types'

interface PageElementsProps {
    page: CmsPage
}

const blockTitle = 'text-xl font-semibold'

// Renders the separate CMS text fields (headings, paragraphs, lists, terms, table, code).
const PageElements: FC<PageElementsProps> = ({ page }): ReactNode => {
    return (
        <>
            {(page.subheading || page.paragraph || page.subheadingTwo || page.paragraphTwo || page.minorHeading || page.smallestHeading) && (
                <div className='flex flex-col gap-6'>
                    {page.subheading && (
                        <h3 className='font-heading text-4xl leading-tight'>
                            {page.subheading}
                        </h3>
                    )}

                    {page.paragraph && (
                        <p className='text-lg leading-relaxed'>
                            {page.paragraph}
                        </p>
                    )}

                    {page.subheadingTwo && (
                        <h4 className='font-heading text-3xl leading-tight'>
                            {page.subheadingTwo}
                        </h4>
                    )}

                    {page.paragraphTwo && (
                        <p className='text-lg leading-relaxed'>
                            {page.paragraphTwo}
                        </p>
                    )}

                    {page.minorHeading && (
                        <h5 className='text-xl font-semibold'>
                            {page.minorHeading}
                        </h5>
                    )}

                    {page.smallestHeading && (
                        <h6 className='text-base font-semibold uppercase tracking-wider text-muted-foreground'>
                            {page.smallestHeading}
                        </h6>
                    )}
                </div>
            )}

            {(page.bulletList.length > 0 || page.numberedList.length > 0) && (
                <div className='grid gap-12 sm:grid-cols-2'>
                    {page.bulletList.length > 0 && (
                        <div className='flex flex-col gap-4'>
                            {page.bulletListTitle && (
                                <h3 className={blockTitle}>
                                    {page.bulletListTitle}
                                </h3>
                            )}

                            <ul className='flex list-disc flex-col gap-2 pl-5 text-lg marker:text-primary'>
                                {page.bulletList.map((item, index) => (
                                    <li key={index}>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {page.numberedList.length > 0 && (
                        <div className='flex flex-col gap-4'>
                            {page.numberedListTitle && (
                                <h3 className={blockTitle}>
                                    {page.numberedListTitle}
                                </h3>
                            )}

                            <ol className='flex list-decimal flex-col gap-2 pl-5 text-lg marker:text-primary'>
                                {page.numberedList.map((item, index) => (
                                    <li key={index}>
                                        {item}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )}

            {page.nestedList.length > 0 && (
                <div className='flex flex-col gap-4'>
                    {page.nestedListTitle && (
                        <h3 className={blockTitle}>
                            {page.nestedListTitle}
                        </h3>
                    )}

                    <ul className='flex list-disc flex-col gap-2 pl-5 text-lg marker:text-primary'>
                        {page.nestedList.map((entry, index) => (
                            <li key={index}>
                                {entry.item}

                                {entry.children.length > 0 && (
                                    <ul className='mt-2 flex list-[circle] flex-col gap-2 pl-5'>
                                        {entry.children.map((child, childIndex) => (
                                            <li key={childIndex}>
                                                {child}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {page.terms.length > 0 && (
                <div className='flex flex-col gap-4'>
                    {page.termsTitle && (
                        <h3 className={blockTitle}>
                            {page.termsTitle}
                        </h3>
                    )}

                    <dl className='flex flex-col gap-4 text-lg'>
                        {page.terms.map((entry, index) => (
                            <div key={index}>
                                <dt className='font-semibold'>
                                    {entry.term}
                                </dt>
                                <dd className='text-muted-foreground'>
                                    {entry.meaning}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}

            {page.tableData && (
                <div className='flex flex-col gap-4'>
                    {page.tableTitle && (
                        <h3 className={blockTitle}>
                            {page.tableTitle}
                        </h3>
                    )}

                    <div className='overflow-x-auto rounded-xl border border-border'>
                        <table className='w-full text-left text-base'>
                            {page.tableData.headers.length > 0 && (
                                <thead className='bg-muted'>
                                    <tr>
                                        {page.tableData.headers.map((header, index) => (
                                            <th key={index} className='px-4 py-3 font-semibold'>
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {page.tableData.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex} className='border-t border-border'>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex} className='px-4 py-3'>
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {page.codeBlock && (
                <div className='flex flex-col gap-4'>
                    {page.codeTitle && (
                        <h3 className={blockTitle}>
                            {page.codeTitle}
                        </h3>
                    )}

                    <pre className='overflow-x-auto rounded-xl bg-foreground p-5 font-mono text-sm leading-relaxed text-background'>
                        <code>{page.codeBlock}</code>
                    </pre>
                </div>
            )}
        </>
    )
}

export default PageElements
